/*
 * Cliente del incremento 4/6 · Meses sin Intereses.
 *
 * Usa la fábrica del núcleo (crearCliente) para NO reimplementar la cookie de
 * sesión, la envoltura común de respuesta ni el aviso de fin de sesión al shell.
 *
 * LAS DOS CONSULTAS QUE NO SE PUEDEN CONFUNDIR (D-70, RN-121):
 *   · /mensualidades → "cuánto hay que PAGAR este mes": solo mensualidades
 *     vigentes, de planes vigentes y con la tarjeta activa.
 *   · /imputacion    → el mismo dato con el nombre con el que lo consumirá el
 *     presupuesto (5/6): cuánto imputa el mes y qué gastos excluir.
 * El reparto del HISTORIAL no se pide desde aquí: lo resuelve el servidor dentro
 * de ConsultarMovimientos (D-69).
 *
 * `fechaLocal` es OBLIGATORIA en todo: sin ella el servidor no puede derivar si
 * una mensualidad está pendiente o cargada, y NUNCA la suple con su reloj (D-06,
 * RN-104b).
 */

import { crearCliente } from '../../../nucleo/api/cliente.js';

const api = crearCliente('/api/msi');

function consulta(parametros) {
  const partes = Object.entries(parametros)
    .filter(([, valor]) => valor !== undefined && valor !== null && valor !== '')
    .map(([clave, valor]) => `${clave}=${encodeURIComponent(valor)}`);

  return partes.length > 0 ? `?${partes.join('&')}` : '';
}

/** Estados del plan. El quinto es DERIVADO y no existe en la base (D-68, RN-119b). */
export const ESTADOS_DE_PLAN = {
  vigente: 'VIGENTE',
  liquidado: 'LIQUIDADO',
  cancelado: 'CANCELADO',
  extinguido: 'EXTINGUIDO_POR_DEVOLUCION',
  cerradoPorBaja: 'CERRADO_POR_BAJA_DE_TARJETA',
};

/** Estados presentados de una mensualidad. "Pagada" NO existe a propósito (S-16). */
export const ESTADOS_DE_MENSUALIDAD = {
  pendiente: 'PENDIENTE',
  cargada: 'CARGADA',
  liquidada: 'LIQUIDADA',
  cancelada: 'CANCELADA',
};

/**
 * GenerarPlanDeMSI (RF-064, RF-065). El número de meses ES EL ÚNICO DATO QUE LA
 * PERSONA CAPTURA en todo el módulo: el importe es el del gasto y las fechas las
 * calcula el 2/6, así que no se mandan nunca (D-56, RNF-035).
 */
export function generarPlan({ idMovimiento, numeroDeMeses, fechaLocal, nota = null, version = null }) {
  return api.publicar('/planes', { idMovimiento, numeroDeMeses, fechaLocal, nota, version });
}

/**
 * SIMULAR el plan ANTES de generarlo (RF-066): devuelve las n mensualidades ya
 * calculadas SIN ESCRIBIR NADA.
 *
 * ES LA VÍA POR LA QUE LA PANTALLA MUESTRA EL DESGLOSE COMPLETO ANTES DE CONFIRMAR
 * SIN DIVIDIR NI UN IMPORTE: el reparto y el calendario los hace el servidor, con
 * la misma pieza de cálculo que la generación, así que lo que se ve y lo que se
 * guarda no pueden diferir (RNF-038, prueba 47).
 */
export function simularPlan({ idMovimiento, numeroDeMeses, fechaLocal }) {
  return api.publicar('/planes/simulacion', { idMovimiento, numeroDeMeses, fechaLocal, nota: null, version: null });
}

/** ConsultarPlanDeMSI por identificador de plan (RF-069). Lectura abierta al hogar. */
export function obtenerPlan(idPlan, fechaLocal) {
  return api.obtener(`/planes/${idPlan}${consulta({ fechaLocal })}`);
}

/** ConsultarPlanDeMSI por gasto. Si el gasto no tiene plan responde PLAN_INEXISTENTE. */
export function obtenerPlanPorMovimiento(idMovimiento, fechaLocal) {
  return api.obtener(`/planes/por-movimiento/${idMovimiento}${consulta({ fechaLocal })}`);
}

/**
 * ConsultarResumenDePlanesDeGastos (D-65, RN-117c). SE PIDE EN LOTE, nunca de uno
 * en uno. Un identificador sin plan NO ES ERROR: sencillamente no viene.
 */
export function consultarResumenDePlanes(idsMovimiento, fechaLocal) {
  return api.obtener(`/planes/resumen${consulta({ idsMovimiento: idsMovimiento.join(','), fechaLocal })}`);
}

/** ConsultarMensualidadesDePeriodo (RF-070): "cuánto hay que pagar este mes". */
export function consultarMensualidades({ idPeriodo, agruparPor, idCuenta, idUsuarioPropietario, fechaLocal } = {}) {
  return api.obtener(`/mensualidades${consulta({ idPeriodo, agruparPor, idCuenta, idUsuarioPropietario, fechaLocal })}`);
}

/**
 * LiquidarMensualidades (RF-071, D-60). NO MUEVE DINERO Y NO TOCA LA DEUDA: la
 * respuesta devuelve `importeLiquidado` para que la pantalla ofrezca el pago de
 * tarjeta del 3/6 con el importe YA PUESTO (RN-110b, RN-110f).
 */
export function liquidarMensualidades(idPlan, { cantidadDeMensualidades, fechaLocal, confirmado = false, nota = null, version }) {
  return api.publicar(`/planes/${idPlan}/liquidaciones`, {
    cantidadDeMensualidades, fechaLocal, confirmado, nota, version,
  });
}

/**
 * AplicarDevolucionAlPlan (RF-072, D-59). La devolución se REGISTRA con el 3/6 y
 * se APLICA al plan aquí: el 3/6 no guarda ese vínculo a propósito y adivinarlo
 * por tarjeta y categoría se equivocaría en silencio (RN-094).
 */
export function aplicarDevolucionAlPlan(idPlan, { idMovimientoDevolucion, fechaLocal, nota = null, version }) {
  return api.publicar(`/planes/${idPlan}/devoluciones`, {
    idMovimientoDevolucion, fechaLocal, nota, version,
  });
}

/**
 * CancelarPlan (RN-108c). EL GASTO NO SE CANCELA: sigue vigente y sigue en la
 * deuda. Sin `confirmado` el servidor responde CONFIRMACION_REQUERIDA con el texto
 * que lo dice.
 */
export function cancelarPlan(idPlan, { fechaLocal, confirmado = false, motivo = null, version }) {
  return api.actualizar(`/planes/${idPlan}/cancelacion`, { fechaLocal, confirmado, motivo, version });
}

/**
 * Recordatorio de RN-124 de UNA tarjeta. ES LA MISMA CIFRA que devuelve el panel
 * del plan: si dos pantallas mostraran números distintos, es defecto (RN-124c).
 * NO EXISTE —ni debe existir— un endpoint para descartarlo (RN-124h.7).
 */
export function obtenerRecordatorioDeTarjeta(idCuenta, fechaLocal) {
  return api.obtener(`/tarjetas/${idCuenta}/recordatorio${consulta({ fechaLocal })}`);
}

// =============================================================================
// INCREMENTO 19/21 · PLAN MSI EN CURSO (contratos §1 y §2)
//
// LO QUE ESTAS DOS OPERACIONES NO MANDAN, Y ES REGLA DURA DE ALCANCE: ningún
// dato de gasto. Capturar el gasto con fecha vieja y generarle plan normal está
// PROHIBIDO por Deisy Paez, palabras literales «duplica deuda y ensucia meses
// que no registro» (RN-711). El plan se ancla a la tarjeta y a su fecha de
// compra declarada, y no crea ningún movimiento.
//
// TAMPOCO MANDAN UN CONCEPTO (D-420): el servidor arma «Compra a meses del
// DD/MM/AAAA» y lo entrega en `textoIdentificacion`. La pantalla no lo compone.
// =============================================================================

/**
 * §1 · GenerarPlanDeMSIEnCurso (RF-255). `mensualidadesCargadas` es el número
 * FINAL corregido por Deisy Paez, no la propuesta (D-406), y 0 es válido
 * (D-416). `importeADescontarDeDiferidoDeclarado` omitido aplica la propuesta
 * `min(respaldo del plan, lo declarado vigente)`; 0 descuenta nada (D-418).
 */
export function generarPlanEnCurso({
  idCuenta, fechaDeCompra, importeTotal, numeroDeMeses, mensualidadesCargadas,
  importeADescontarDeDiferidoDeclarado = null, fechaLocal, nota = null,
}) {
  return api.publicar('/planes/en-curso', {
    idCuenta, fechaDeCompra, importeTotal, numeroDeMeses, mensualidadesCargadas,
    importeADescontarDeDiferidoDeclarado, fechaLocal, nota,
  });
}

/**
 * §2 · SimularPlanDeMSIEnCurso (RF-256, D-413). SOLO LECTURA: no guarda nada.
 * Con `mensualidadesCargadas` omitido devuelve la PROPUESTA de la aplicación
 * (`mensualidadesCargadasPropuestas`, nula cuando no se pudo derivar) y el
 * calendario calculado con ella; con el número corregido, el calendario
 * resultante y el aviso de frontera, que avisa y NO bloquea (RN-717, RN-731).
 */
export function simularPlanEnCurso({
  idCuenta, fechaDeCompra, importeTotal, numeroDeMeses,
  mensualidadesCargadas = null, importeADescontarDeDiferidoDeclarado = null, fechaLocal,
}) {
  return api.publicar('/planes/en-curso/simulacion', {
    idCuenta, fechaDeCompra, importeTotal, numeroDeMeses, mensualidadesCargadas,
    importeADescontarDeDiferidoDeclarado, fechaLocal,
  });
}
